import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { aboutData, testimonials } from '@/mocks/data';

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <>
      <SEOSchema
        type="about"
        title="关于韩漫屋 - 品牌故事与商务合作"
        description="韩漫屋创建于2020年，专注韩国漫画在线阅读，拥有超过3200部漫画作品和50万忠实用户。了解韩漫屋的品牌故事、平台数据和商务合作方式。"
        url="http://fkyy11.com/about"
        breadcrumbs={[
          { name: t('common:breadcrumbHome'), url: 'http://fkyy11.com/' },
          { name: t('common:navAbout'), url: 'http://fkyy11.com/about' },
        ]}
        faqs={[
          { question: t('common:faqQ1'), answer: t('common:faqA1') },
          { question: t('common:faqQ2'), answer: t('common:faqA2') },
          { question: t('common:faqQ3'), answer: t('common:faqA3') },
          { question: t('common:faqQ4'), answer: t('common:faqA4') },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: t('common:breadcrumbHome'), href: '/' },
            { label: t('common:navAbout') },
          ]}
        />

        <section className="relative w-full h-[420px] md:h-[500px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20team%20workspace%20with%20artists%20and%20editors%20working%20on%20digital%20illustrations%2C%20shelves%20of%20comic%20books%2C%20soft%20ambient%20lighting%2C%20creative%20atmosphere%2C%20modern%20office%20with%20plants%2C%20warm%20amber%20and%20cream%20tones%2C%20digital%20art%2C%20cozy%20aesthetic&width=1800&height=700&seq=about-hero-2026&orientation=landscape"
            alt="韩漫屋团队"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/35 to-foreground-950/65"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight">
              {t('common:aboutPageTitle')}
            </h1>
            <p className="mt-3 md:mt-4 text-sm md:text-lg text-background-50/75 max-w-xl">
              {aboutData.brandStory.subtitle}
            </p>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100/70 rounded-full mb-4">
                <i className="ri-book-read-line text-primary-500 text-sm"></i>
                <span className="text-xs font-medium text-primary-700">{t('common:aboutBrandTitle')}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {aboutData.brandStory.title}
              </h2>
            </div>

            <div className="space-y-5">
              {aboutData.brandStory.paragraphs.map((para, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-100/70 rounded-full mb-4">
                <i className="ri-bar-chart-line text-accent-600 text-sm"></i>
                <span className="text-xs font-medium text-accent-700">{t('common:aboutPlatformTitle')}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {t('common:aboutPlatformTitle')}
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto">
              {aboutData.platformStats.map((stat, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                    <i className={`${stat.icon} text-primary-500 text-xl`}></i>
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-900">{stat.value}</p>
                  <p className="text-xs md:text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-secondary-100/70 rounded-full mb-4">
                <i className="ri-contacts-line text-secondary-600 text-sm"></i>
                <span className="text-xs font-medium text-secondary-700">{t('common:aboutBusinessTitle')}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {aboutData.businessInfo.title}
              </h2>
              <p className="mt-2 text-sm text-foreground-500">{aboutData.businessInfo.subtitle}</p>
            </div>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
              {aboutData.businessInfo.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {aboutData.businessInfo.contactItems.map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-background-100 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-secondary-100/50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                漫友好评
              </h2>
              <p className="text-sm text-foreground-500 mt-1">听听读者们怎么说</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto">
              {testimonials.slice(0, 3).map((item) => (
                <div key={item.id} className="bg-background-50 rounded-lg p-5 md:p-6 relative">
                  <i className="ri-double-quotes-l text-3xl text-primary-200 absolute top-4 right-5"></i>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: item.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-50 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-primary-100/40 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent-100/30 rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
              加入韩漫屋大家庭
            </h2>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
              与50万漫迷一起，在韩漫屋发现属于你的精彩漫画世界
            </p>
            <div className="mt-7 md:mt-9">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm md:text-base hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('common:ctaButton')}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}